import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ChevronLeft, Loader2, Check, AlertTriangle } from 'lucide-react';
import { YourCheckaNavigation } from '@/components/Navbar';
import { Badge } from '@/components/ui/badge';
import { useAppSelector } from '@/hooks/useAppStore';
import { selectCurrentUser } from '@/store/slices/authSlice';
import {
    useGetMedicalInvestigationQuery,
    useGetMedicalTestsQuery,
    useGetMedicalReportQuery,
    useSubmitMedicalReportMutation,
    useGetMedicalLabQuery,
} from '@/store/api/apiSlice';
import MedicalLabSection from '@/components/MedicalLabSection';
import { parseErrorMessage } from '@/utils/parseErrorMessage';
import type { MedicalTestResult } from '@/types';

function getInitials(firstName?: string, lastName?: string) {
    const first = firstName?.charAt(0) || '';
    const last = lastName?.charAt(0) || '';
    return (first + last).toUpperCase() || 'MD';
}

function fullName(first?: string, last?: string) {
    return `${first ?? ''} ${last ?? ''}`.trim();
}

// Local editable result row: result + optional status + optional notes.
type ResultDraft = { result: string; status: string; notes: string };

function emptyDraft(): ResultDraft {
    return { result: '', status: '', notes: '' };
}

export default function MedicalDetail() {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const currentUser = useAppSelector(selectCurrentUser);

    const { data: investigation, isLoading: invLoading } = useGetMedicalInvestigationQuery(id || '', { skip: !id });
    const { data: panel, isLoading: panelLoading } = useGetMedicalTestsQuery(id || '', { skip: !id });
    const { data: report, isLoading: reportLoading } = useGetMedicalReportQuery(id || '', { skip: !id });
    const { data: assignedLab } = useGetMedicalLabQuery(investigation?.medicalLabId || '', {
        skip: !investigation?.medicalLabId,
    });
    const [submitReport, { isLoading: isSubmitting }] = useSubmitMedicalReportMutation();

    const [results, setResults] = useState<Record<string, ResultDraft>>({});
    const [summary, setSummary] = useState('');
    const [facilityName, setFacilityName] = useState('');
    const [testedAt, setTestedAt] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [missingKeys, setMissingKeys] = useState<string[]>([]);

    const tests = useMemo(() => panel?.tests ?? [], [panel]);

    // Hydrate the form once the panel (+ any existing report) loads. Seed an empty
    // draft per test key so every required test renders even on a fresh report.
    useEffect(() => {
        if (tests.length === 0) return;
        const seeded: Record<string, ResultDraft> = {};
        tests.forEach((t) => {
            const existing = report?.results?.[t.key];
            seeded[t.key] = existing
                ? {
                      result: existing.result ?? '',
                      status: existing.status ?? '',
                      notes: existing.notes ?? '',
                  }
                : emptyDraft();
        });
        setResults(seeded);
        if (report) {
            setSummary(report.summary ?? '');
            setFacilityName(report.facilityName ?? '');
            setTestedAt(report.testedAt ? report.testedAt.slice(0, 10) : '');
        }
    }, [tests, report]);

    // Prefill the facility with the assigned lab name when the report doesn't
    // already carry one — mirrors the backend's auto-fill on submission.
    useEffect(() => {
        if (assignedLab?.name && !report?.facilityName) {
            setFacilityName((prev) => prev || assignedLab.name);
        }
    }, [assignedLab, report]);

    const updateResult = (key: string, field: keyof ResultDraft, value: string) => {
        setResults((prev) => ({ ...prev, [key]: { ...(prev[key] ?? emptyDraft()), [field]: value } }));
        setSuccess('');
        if (missingKeys.includes(key) && field === 'result' && value.trim()) {
            setMissingKeys((prev) => prev.filter((k) => k !== key));
        }
    };

    const handleSubmit = async () => {
        if (!id) return;
        setError('');
        setSuccess('');

        // Mirror the backend gate: every test key must have a non-empty result.
        const missing = tests.filter((t) => !(results[t.key]?.result ?? '').trim()).map((t) => t.key);
        if (missing.length > 0) {
            setMissingKeys(missing);
            setError('Every test must have a result before you can submit.');
            return;
        }
        setMissingKeys([]);

        const payloadResults: Record<string, MedicalTestResult> = {};
        tests.forEach((t) => {
            const draft = results[t.key] ?? emptyDraft();
            payloadResults[t.key] = {
                result: draft.result.trim(),
                ...(draft.status.trim() ? { status: draft.status.trim() } : {}),
                ...(draft.notes.trim() ? { notes: draft.notes.trim() } : {}),
            };
        });

        try {
            await submitReport({
                id,
                data: {
                    results: payloadResults,
                    ...(summary.trim() ? { summary: summary.trim() } : {}),
                    ...(facilityName.trim() ? { facilityName: facilityName.trim() } : {}),
                    ...(testedAt ? { testedAt: new Date(testedAt).toISOString() } : {}),
                },
            }).unwrap();
            setSuccess('Medical report submitted.');
        } catch (err) {
            setError(parseErrorMessage(err, 'Failed to submit the report.'));
        }
    };

    const isLoading = invLoading || panelLoading || reportLoading;

    if (isLoading) {
        return (
            <main className="w-full h-screen flex items-center justify-center bg-[#EDEFF3]">
                <Loader2 className="h-10 w-10 text-[#FF6B35] animate-spin" />
            </main>
        );
    }

    if (!investigation) {
        return (
            <main className="w-full h-screen flex items-center justify-center bg-[#EDEFF3]">
                <div className="text-center">
                    <p className="text-red-600 mb-4">Failed to load investigation.</p>
                    <button
                        onClick={() => navigate('/medical/investigations')}
                        className="px-6 py-2 bg-gray-900 text-white rounded-xl hover:bg-gray-800"
                    >
                        Back to medical reports
                    </button>
                </div>
            </main>
        );
    }

    const employeeName =
        fullName(investigation.employeeFirstName, investigation.employeeLastName) || investigation.employeeEmail;

    return (
        <main className="w-full min-h-screen flex flex-col px-4 py-6 md:px-6 lg:px-8 xl:px-12 bg-[#EDEFF3]">
            <div className="bg-white rounded-3xl shadow-sm flex-1 flex flex-col overflow-hidden">
                <YourCheckaNavigation
                    showTitle={false}
                    showInfo={false}
                    showLocation={false}
                    notificationCount={0}
                    userName={currentUser ? `${currentUser.firstName} ${currentUser.lastName}` : 'Medical'}
                    userInitials={currentUser ? getInitials(currentUser.firstName, currentUser.lastName) : 'MD'}
                    only={true}
                    className="border-b border-gray-100"
                />

                <div className="flex-1 overflow-y-auto p-4 md:p-8 lg:p-10 flex flex-col gap-6 w-full">
                    {/* Header */}
                    <div className="flex items-start gap-4">
                        <button
                            onClick={() => navigate('/medical/investigations')}
                            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200"
                        >
                            <ChevronLeft className="h-5 w-5 text-gray-600" />
                        </button>
                        <div className="flex flex-wrap items-center gap-3">
                            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">{employeeName}</h1>
                            <Badge
                                variant="outline"
                                className="bg-gray-50 text-gray-700 border-gray-200 px-3 py-1 rounded-lg font-medium capitalize"
                            >
                                {(panel?.category ?? investigation.investigationCategory ?? 'employment')
                                    .toString()
                                    .replace('_', ' ')}
                            </Badge>
                            {report && (
                                <Badge
                                    variant="outline"
                                    className="bg-green-50 text-green-700 border-green-100 px-3 py-1 rounded-lg font-medium"
                                >
                                    Submitted
                                </Badge>
                            )}
                        </div>
                    </div>

                    {/* Medical lab assignment */}
                    <MedicalLabSection
                        investigationId={investigation.id}
                        variant="medical"
                        locationHint={investigation.employmentTown}
                        medicalLabId={investigation.medicalLabId}
                        medicalLabAssignedAt={investigation.medicalLabAssignedAt}
                    />

                    {/* Required test panel */}
                    <div className="rounded-2xl border border-gray-100 p-5 flex flex-col gap-4">
                        <div>
                            <h3 className="text-sm font-bold text-gray-900">Required tests</h3>
                            <p className="text-xs text-gray-500 mt-0.5">
                                All tests for this category are required. Enter a result for each.
                            </p>
                        </div>

                        {tests.length === 0 ? (
                            <p className="text-sm text-gray-400">No test panel available for this investigation.</p>
                        ) : (
                            <div className="flex flex-col gap-4">
                                {tests.map((test) => {
                                    const draft = results[test.key] ?? emptyDraft();
                                    const isMissing = missingKeys.includes(test.key);
                                    return (
                                        <div
                                            key={test.key}
                                            className={`rounded-xl border p-4 flex flex-col gap-3 ${
                                                isMissing ? 'border-red-300 bg-red-50/40' : 'border-gray-100'
                                            }`}
                                        >
                                            <label className="text-sm font-semibold text-gray-900">{test.label}</label>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                <input
                                                    type="text"
                                                    value={draft.result}
                                                    onChange={(e) => updateResult(test.key, 'result', e.target.value)}
                                                    placeholder="Result (e.g. Negative, 42%)"
                                                    className={`w-full border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#FF6B35] ${
                                                        isMissing ? 'border-red-300' : 'border-gray-200'
                                                    }`}
                                                />
                                                <select
                                                    value={draft.status}
                                                    onChange={(e) => updateResult(test.key, 'status', e.target.value)}
                                                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-white focus:outline-none focus:border-[#FF6B35]"
                                                >
                                                    <option value="">Status (optional)</option>
                                                    <option value="normal">Normal</option>
                                                    <option value="abnormal">Abnormal</option>
                                                    <option value="inconclusive">Inconclusive</option>
                                                </select>
                                            </div>
                                            <input
                                                type="text"
                                                value={draft.notes}
                                                onChange={(e) => updateResult(test.key, 'notes', e.target.value)}
                                                placeholder="Notes (optional)"
                                                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#FF6B35]"
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* Report meta */}
                    <div className="rounded-2xl border border-gray-100 p-5 flex flex-col gap-4">
                        <h3 className="text-sm font-bold text-gray-900">Report details</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="flex flex-col gap-1.5">
                                <label className="text-xs font-medium text-gray-700">Facility name</label>
                                <input
                                    type="text"
                                    value={facilityName}
                                    onChange={(e) => {
                                        setFacilityName(e.target.value);
                                        setSuccess('');
                                    }}
                                    placeholder="e.g. XYZ Diagnostics, Ikeja"
                                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#FF6B35]"
                                />
                            </div>
                            <div className="flex flex-col gap-1.5">
                                <label className="text-xs font-medium text-gray-700">Tested at</label>
                                <input
                                    type="date"
                                    value={testedAt}
                                    onChange={(e) => {
                                        setTestedAt(e.target.value);
                                        setSuccess('');
                                    }}
                                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#FF6B35]"
                                />
                            </div>
                        </div>
                        <div className="flex flex-col gap-1.5">
                            <label className="text-xs font-medium text-gray-700">Summary</label>
                            <textarea
                                value={summary}
                                onChange={(e) => {
                                    setSummary(e.target.value);
                                    setSuccess('');
                                }}
                                rows={3}
                                placeholder="e.g. Fit for engagement."
                                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm resize-none focus:outline-none focus:border-[#FF6B35]"
                            />
                        </div>
                    </div>

                    {error && (
                        <div className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                            {error}
                        </div>
                    )}
                    {success && (
                        <div className="flex items-center gap-2 rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
                            <Check className="w-4 h-4 shrink-0" />
                            {success}
                        </div>
                    )}

                    <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={isSubmitting || tests.length === 0}
                        className="self-start bg-[#FF6B35] hover:bg-[#FF5722] disabled:opacity-50 disabled:cursor-not-allowed text-white px-6 py-3 rounded-xl text-sm font-medium flex items-center gap-2"
                    >
                        {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
                        {report ? 'Update report' : 'Submit report'}
                    </button>
                </div>
            </div>
        </main>
    );
}
